import React, { useState } from 'react';
import { collectFromDataTransfer } from '../../utils/treeUpload/collectEntries';
import { archiveKind } from '../../utils/treeUpload/readArchive';
import ImportProjectDialog from './ImportProjectDialog';
import './ImportProjectDialog.css';

const entriesOf = (dataTransfer) => Array.from(dataTransfer?.items || [])
  .filter((item) => item.kind === 'file')
  .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null));

/**
 * True when a drop is a project's code rather than something to attach: a folder, or one .zip / .tar.gz.
 * Read synchronously inside the drop handler (the entries are only readable during the event).
 */
export function isProjectSourceDrop(dataTransfer) {
  const entries = entriesOf(dataTransfer);
  if (entries.some((entry) => entry?.isDirectory)) { return true; }
  const files = Array.from(dataTransfer?.files || []);
  const names = files.length ? files.map((file) => file.name) : entries.filter(Boolean).map((entry) => entry.name);
  return names.length === 1 && ['zip', 'tar', 'tgz'].includes(archiveKind(names[0]));
}

/**
 * The Home prompt's "Start a project from it": a folder or a zip dropped on the prompt is offered as a new project
 * (the New project › Import flow, ImportProjectDialog) instead of being attached. A zip can still be attached.
 *
 * `takeDrop(dataTransfer)` (call it first in the prompt's drop handler) returns true when it took the drop; `element`
 * goes inside the prompt box (the offer and the dialog).
 */
export default function useProjectImportOffer({ selectedTenant = null, onAttach } = {}) {
  const [offer, setOffer] = useState(null); // {collected, label, archive}
  const [dialogOpen, setDialogOpen] = useState(false);

  const takeDrop = (dataTransfer) => {
    if (!isProjectSourceDrop(dataTransfer)) { return false; }
    collectFromDataTransfer(dataTransfer).then((collected) => {
      if (!collected.files.length) { return; }
      const archive = collected.folderNames.length ? null : collected.files[0];
      const label = archive ? archive.relPath : (collected.folderNames.find((name) => !name.includes('/')) || collected.files[0].relPath.split('/')[0]);
      setOffer({ collected, label, archive });
    }).catch(() => null);
    return true;
  };

  const element = (
    <>
      {offer && !dialogOpen && (
        <div className="sbx-import-offer" data-testid="home-import-offer" role="status">
          <span className="sbx-import-offer-label">{offer.label}</span>
          <button className="sbx-import-offer-primary" onClick={() => setDialogOpen(true)} type="button">Start a project from it</button>
          {offer.archive && onAttach && (
            <button className="sbx-import-offer-link" onClick={() => { onAttach([offer.archive.file]); setOffer(null); }} type="button">Attach instead</button>
          )}
          <button aria-label="Dismiss" className="sbx-import-offer-link" onClick={() => setOffer(null)} type="button">✕</button>
        </div>
      )}
      <ImportProjectDialog
        initialCollected={dialogOpen ? offer?.collected : null}
        onClose={() => { setDialogOpen(false); setOffer(null); }}
        open={dialogOpen}
        selectedTenant={selectedTenant}
      />
    </>
  );

  return { takeDrop, element };
}
